import type { MedplumClient } from '@medplum/core';
import {
  type IdentifiedAppointment,
  type ManagePatientAppointmentInput,
  type ManagePatientAppointmentResult,
  type ProposedAppointment,
  parseSchedulingError,
  SCHEDULING_ERROR_CATEGORIES,
  SCHEDULING_OPERATION_NAMES,
  SchedulingError,
} from '@polarissleep/scheduling-next';
import * as Sentry from '@sentry/react';
import { botIdentifier } from '../lib/bot-identifier';

const MANAGE_PATIENT_APPOINTMENT_BOT = 'manage-patient-appointment';

export async function bookAppointmentThroughBot(
  medplum: MedplumClient,
  appointment: ProposedAppointment,
  timeZone: string | undefined
): Promise<IdentifiedAppointment> {
  return executeManagePatientAppointment(
    medplum,
    { action: 'book', appointment, ...(timeZone ? { timeZone } : {}) },
    SCHEDULING_OPERATION_NAMES.book
  );
}

export async function cancelAppointmentThroughBot(
  medplum: MedplumClient,
  appointmentId: string,
  reason?: string
): Promise<IdentifiedAppointment> {
  return executeManagePatientAppointment(
    medplum,
    { action: 'cancel', appointmentId, ...(reason ? { reason } : {}) },
    SCHEDULING_OPERATION_NAMES.cancel
  );
}

async function executeManagePatientAppointment(
  medplum: MedplumClient,
  input: ManagePatientAppointmentInput,
  operation: string
): Promise<IdentifiedAppointment> {
  return Sentry.startSpan(
    { op: 'bot.execute', name: `Execute ${MANAGE_PATIENT_APPOINTMENT_BOT} ${input.action}` },
    async () => {
      try {
        const result = (await medplum.executeBot(
          botIdentifier(MANAGE_PATIENT_APPOINTMENT_BOT),
          input,
          'application/json'
        )) as ManagePatientAppointmentResult;
        return result.appointment;
      } catch (error) {
        throw toSchedulingError(error, operation);
      }
    }
  );
}

// The bot puts the scheduling error category in its error text; recover it so callers map it as before.
function toSchedulingError(error: unknown, operation: string): SchedulingError {
  const parsed = parseSchedulingError(error, { operation, resourceType: 'Appointment' });
  const category = Object.values(SCHEDULING_ERROR_CATEGORIES).find((candidate) =>
    parsed.diagnostics?.includes(candidate)
  );
  if (!category) {
    return parsed;
  }
  return new SchedulingError(category, parsed.message, {
    outcome: parsed.outcome,
    diagnostics: parsed.diagnostics,
    cause: error,
  });
}
